import { isSameWeek } from "date-fns";
import { TimeReport } from "~/core";
import { getColorFor } from "~/core/color-manager";
import { store } from "~/core/store";
import { dateFromApi, getCodeString } from "~/utils";
import {
    HoursDistributionGroupBy,
    HoursDistributionRangeType,
} from "./interfaces";

const checkRangeDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

const checkRangeWeek = (a: Date, b: Date) => isSameWeek(a, b);

const checkRangeMonth = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

const checkRangeYear = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear();

const checkRangeAccumulated = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    b <= a;

const getHoursDistribution = (
    date: Date,
    groupBy: HoursDistributionGroupBy,
    range: HoursDistributionRangeType,
) => {
    const { time, project, feeCode, client } = store.api;
    const clients = client.clients.peek();
    const projects = project.projects.peek();
    const codes = feeCode.codes.peek();
    const checkRange =
        range === "day"
            ? checkRangeDay
            : range === "week"
              ? checkRangeWeek
              : range === "month"
                ? checkRangeMonth
                : range === "year"
                  ? checkRangeYear
                  : checkRangeAccumulated;
    const reports = time.report.reports
        .peek()
        .filter(
            (report): report is TimeReport & { line_type: { $case: "fee" } } =>
                report.line_type?.$case === "fee" &&
                checkRange(date, dateFromApi(report.date)),
        );

    const ids: string[] = [];
    const labels: string[] = [];
    const longLabels: string[] = [];
    const values: number[] = [];
    const colors: string[] = [];
    const projectsMap: Record<string, number> = {};
    const codesMap: Record<string, number> = {};

    if (groupBy === "project") {
        for (const report of reports) {
            const projectKey = report.project.toString();

            if (!(projectKey in projectsMap)) {
                projectsMap[projectKey] = 0;
            }

            projectsMap[projectKey] +=
                report.line_type.fee.hours?.toNumber() ?? 0;
        }

        const sorted = Object.entries(projectsMap).sort(
            ([, a], [, b]) => b - a,
        );

        for (const [key, value] of sorted) {
            const project = projects.find((project) => project.id === key);

            if (!project) {
                continue;
            }

            const pClient = clients.find((c) => c.client === project.client);

            ids.push(project.id);
            labels.push(`${project.client} ${project.name}`);
            longLabels.push(
                `${pClient ? pClient.name_internal : project.client} ${getCodeString(project.project)} ${project.name}`,
            );
            values.push(value);
            colors.push(getColorFor("project", project.id));
        }
    } else {
        for (const report of reports) {
            if (!report.line_type.fee.feecode) {
                continue;
            }

            const codeKey = report.line_type.fee.feecode.toString();

            if (!(codeKey in codesMap)) {
                codesMap[codeKey] = 0;
            }

            codesMap[codeKey] += report.line_type.fee.hours?.toNumber() ?? 0;
        }

        const sorted = Object.entries(codesMap).sort(([, a], [, b]) => b - a);

        for (const [key, value] of sorted) {
            const code = codes.find((code) => code.id === key);

            if (!code) {
                continue;
            }

            ids.push(code.id);
            labels.push(code.name_global);
            longLabels.push(
                `${getCodeString(code.code, 3)} ${code.name_global}`,
            );
            values.push(value);
            colors.push(getColorFor("feecode", code.id));
        }
    }

    return { ids, labels, longLabels, values, colors };
};

export default getHoursDistribution;
